import React, { Component } from 'react';
import { func, object } from 'prop-types';
import {
  addIndex,
  any,
  concat,
  contains,
  filter,
  inc,
  isEmpty,
  lensProp,
  map,
  set,
  splitWhen,
  tail,
  update,
} from 'ramda';
import moment from 'moment';
import Select from 'react-select';

// Redux
import { connect } from 'react-redux';
import * as AppointmentsTypes from '../../../../actions/appointments/types';
import {
  getAllSlots,
  getFilter,
  getFilterLabels,
  requestAppointment,
  getAppointmentTimeRange,
  getUpcomingAppointments,
} from '../../../../actions/appointments/appointmentsActions';

import LoadingState from '../../../LoadingState';
import SchedulingRules from './Rules';
import SchedulingNotes from './Notes';
import SchedulingCalendar from './Calendar';
import AppointmentRequest from '../AppointmentRequest';

import cx from 'classnames';
import s from './SchedulingContainer.css';


export class SchedulingContainer extends Component {
  static propTypes = {
    appointments: object,
    authenticatedPreferences: object,
    closeAlertDialog: func,
    closeConfirmDialog: func,
    getAllSlots: func,
    getFilter: func,
    getAppointmentTimeRange: func,
    getFilterLabels: func,
    getUpcomingAppointments: func,
    loadingState: object,
    openConfirmDialog: func,
    openSuccessDialog: func,
  };

  // The appointmentRequest is built from state objects from each filter, time slot and notes.
  static appointmentRequest = {
    selectedFirstValue: {},
    selectedSecondValue: {},
    selectedThirdValue: {},
    selectedFourthValue: null,
    selectedDate: null,
    slot: null,
    notes: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      appointmentRequest: {},
      appointmentSlots: undefined,
      fields: [],
      getAllSlotsError: null,
      getFilterError: null,
      whenSelected: undefined,
      minTimeInAdvanceToScheduleInHours: 0,
      maxTimeInAdvanceToScheduleInHours: 0,
    };
  }

  // When we mount, we fetch the filter labels. On success, we chain a call to get the first filter
  componentDidMount() {

    this.handleGetFilterLabels();

  }

  // Append selected state values to appointmentRequest object
  buildAppointmentRequest = requestObject => {

    let appointmentRequest;

    const item1 = this.state.fields[ 0 ].selected;
    const item2 = this.state.fields[ 1 ].selected;
    const item3 = this.state.fields[ 2 ].selected;

    // Optional filter 4
    const value4 = this.state.fields[ 3 ];
    const item4 = value4 === undefined ? null : value4.selected;

    // Build the appointmentRequest object
    requestObject.selectedFirstValue = item1;
    requestObject.selectedSecondValue = item2;
    requestObject.selectedThirdValue = item3;
    requestObject.selectedFourthValue = item4;

    // Extract the date range for the particular when selection
    const beginDate = this.state.whenSelected.beginDate;
    const endDate = this.state.whenSelected.endDate;

    appointmentRequest = {
      item1,
      item2,
      item3,
      item4,
      beginDate,
      endDate,
    };

    return appointmentRequest;

  };

  // We setState with appointments.slots so when onChange happens in a filter we can reset the SchedulingCalendar UI
  getAllSlotsSuccessCallback = () => this.setState({ appointmentSlots: this.props.appointments.slots });

  getAllSlotsErrorCallback = response => this.setState({ getAllSlotsError: response.message.body });

  getFilter = (index, fields) => {

    const hasSelected = field => !isEmpty(field.selected);
    const filters = filter(hasSelected, fields);

    const payload = map(field => {
      return field.selected;
    }, filters);

    // Format object for making getFilter api request
    const data = {
      id: index,
      items: payload,
    };

    // We have access to the response object, but it is bound in the reducer to filterItems
    const successCallback = () => {

      const label = fields[index].label;

      // for clearing out the filters when reselecting
      const [saved, outdated] = splitWhen(field =>
        field.label === label, fields);

      // Reformat value list to match react-select reqs
      const responseObject = this.props.appointments.filterItems.map((filter, index) => {
        return {
          value: filter,
          label: filter.label,
        };
      });

      const current = {
        label: label,
        items: responseObject,
        selected: '',
      };

      // Filter labels not current.items
      if (current.items.length === 1) {
        this.handleFieldChange(index, current, current.items[0].value, fields);
      } else {
        // ramda is removing
        const cleared = tail(map(field => {
          return {
            label: field.label,
            items: [],
            selected: '',
          }
        }, outdated));

        // Save updated fields to internal state
        this.setState({
          fields: concat(saved, concat([current], cleared)),
        });
      }

      const setTimeInAdvanceToScheduleInHours = (min,max) => {
        this.setState({
            minTimeInAdvanceToScheduleInHours: min,
            maxTimeInAdvanceToScheduleInHours: max,
        });
      };

      //If the location was changed, we need to get the appointmentTimeRange values for that location and update the "When" dropdown
      if (data.id === 1) {
        const locationFilterItemIndex = 0;
        const locationFilterItem = data.items[locationFilterItemIndex];
        const locationId = locationFilterItem.id;
        const getAppointmentTimeRangeRequest = {locationId};
        this.props.getAppointmentTimeRange(getAppointmentTimeRangeRequest,
            (data) => { setTimeInAdvanceToScheduleInHours(data.minTimeInAdvanceToScheduleInHours,data.maxTimeInAdvanceToScheduleInHours)},
            errorCallback
        );
      }
    };

    const errorCallback = response => this.setState({ getFilterError: response.message.body });

    this.props.getFilter(data, successCallback, errorCallback);

  };

  handleGetFilterLabels = () => {

    const successCallback = () => {
      const reactSelectFields = map(label => {
        return { label, items: [], selected: ''}
      }, this.props.appointments.labels);

      this.setState({
        fields: reactSelectFields,
      });

      // get the first filter
      this.getFilter(0, this.state.fields);
    };

    this.props.getFilterLabels(successCallback);

  };

  // Appends selected time and day and sets state to pass to AppointmentRequest
  // @param selectedSlot example = {id: null, start: 660, duration: 30}
  handleTimeSlotSelection = selection => {

    SchedulingContainer.appointmentRequest.slot = selection.slot;
    SchedulingContainer.appointmentRequest.selectedDate = selection.date;

    this.setState({ appointmentRequest: SchedulingContainer.appointmentRequest });

  };

  // Append notes to appointmentRequest
  handleNotes = notesAdded => SchedulingContainer.appointmentRequest.notes = notesAdded;

  // The onChange event will never be "done" until the ajax event from getFilter() is complete. This, in tandem with the setState cycle
  // of this parent component, down to the child AppointmentRequest for disabled button state is a step behind, always.
  handleFieldChange = (index, field, selected, fields) => {
    const updatedField = set(lensProp('selected'), selected, field);
    const updatedFields = update(index, updatedField, fields);
    const newIndex = inc(index);

    if (newIndex < this.state.fields.length) {
      // Re-selecting should clear appointmentSlots and whenSelected
      this.setState({
        appointmentRequest: {},
        appointmentSlots: undefined,
        whenSelected: undefined,
        getAllSlotsError: null,
      });

      this.getFilter(newIndex, updatedFields);

    } else {
      this.setState({ fields: updatedFields });
    }
  };

  // Pass the appointmentRequest object on to getAllSlots call after whenSelected has a value
  handleWhenSelection = value => {

    this.setState({
      whenSelected: value,
    }, () => {
      const data = this.buildAppointmentRequest(SchedulingContainer.appointmentRequest);

      this.props.getAllSlots(data, this.getAllSlotsSuccessCallback, this.getAllSlotsErrorCallback);
    });

  };

  isLoadingFilter = loadingState => contains(AppointmentsTypes.GET_FILTER, loadingState.requests);

  isLoadingFilterLabels = loadingState => contains(AppointmentsTypes.GET_FILTER_LABELS, loadingState.requests);

  isLoadingGetAllSlots = loadingState => contains(AppointmentsTypes.GET_ALL_SLOTS, loadingState.requests);
  renderStrategizedFilters = () => {
    const isLoadingFilter = this.isLoadingFilter(this.props.loadingState);
    const isLoadingFilterLabels = this.isLoadingFilterLabels(this.props.loadingState);

    const mapIndexed = addIndex(map);

    const generateMenu = (minHoursAdvanceToSchedule, maxHoursInAdvanceToSchedule) => {
      const now = moment();

      //There is no time that is between the min and max, so there are no weeks that will have slots
      if(minHoursAdvanceToSchedule > maxHoursInAdvanceToSchedule){
        return [];
      }

      const thisWeek = now.clone().day(1).hour(0);
      const nextWeek = thisWeek.clone().add(1, 'weeks');
      const firstWeekToScheduleFor = now.clone().add(minHoursAdvanceToSchedule, 'hours').day(1).hour(0);
      const lastWeekToScheduleFor = now.clone().add(maxHoursInAdvanceToSchedule, 'hours').day(1).hour(0);

      let data = [];

      for(let currentId = 1, currentWeek = firstWeekToScheduleFor.clone(); currentWeek.isSameOrBefore(lastWeekToScheduleFor); currentId++, currentWeek.add(1, 'week')) {
        //If the week we are on is the first or second week, let's create a label signifying that
        let currentLabel = "";
        if(currentWeek.isSame(thisWeek)) {
          currentLabel = 'This Week';
        } else if (currentWeek.isSame(nextWeek)) {
          currentLabel = 'Next Week';
        }
        //Otherwise use the generic "week of" label
        else {
          currentLabel = 'Week of ' + currentWeek.format('MMM Do');
        }

        let object = {
          id: currentId,
          label: currentLabel,
          beginDate: currentWeek.clone().day(1),
          endDate: currentWeek.clone().day(7),
        };

        data.push(object);
      }

      return data;
    };

    if (isLoadingFilterLabels) {
      return (
        <section className={s.filtersContainer}>
          <p className={s.message}>
            Loading Appointment options...
          </p>
          <LoadingState context="scheduling" />
        </section>
      );
    }
    else if (this.state.getFilterError) {
      return (
        <section className={s.filtersContainer}>
          <p className={cx(s.message, s.alert)}>
            {this.state.getFilterError}
          </p>
        </section>
      );
    }
    else {
      return (
        <section className={s.filtersContainer}>
          {mapIndexed((field, index) =>
            <nav className={s.group} id={`schedulingFilter__${field.label}`} key={field.label}>
              <label htmlFor={field.label} className={s.label}>
                {field.label}:
              </label>
              {isLoadingFilter ? (
                <div className={s.flex}>
                  <p className={s.message}>
                    Loading filter...
                  </p>
                  <LoadingState context="schedulingFilter" />
                </div>
              ) : (
                <Select
                  id={field.label}
                  className={cx(s.control, "schedulingFilter")}
                  clearable={false}
                  disabled={isEmpty(field.items)}
                  onChange={item => this.handleFieldChange(index, field, item.value, this.state.fields)}
                  optionClassName="schedulingFilter__option"
                  options={field.items}
                  searchable={false}
                  value={field.selected}
                />
              )}
            </nav>, this.state.fields)
          }
            <nav className={s.group} id="schedulingFilter__When">
              <label className={s.label}>
                When:
              </label>
              <Select
                className={cx(s.control, "schedulingFilter")}
                clearable={false}
                disabled={any(field => isEmpty(field.selected), this.state.fields)}
                name="When"
                onChange={this.handleWhenSelection}
                optionClassName="schedulingFilter__option"
                options={generateMenu(this.state.minTimeInAdvanceToScheduleInHours, this.state.maxTimeInAdvanceToScheduleInHours)}
                searchable={false}
                value={this.state.whenSelected}
              />
            </nav>
        </section>
      );
    }
  };

  resetSchedulingUi = () =>
    this.setState({
      appointmentRequest: {},
      appointmentSlots: undefined,
      whenSelected: undefined,
      getAllSlotsError: null,
    });

  render = () => {
    const { authenticatedPreferences } = this.props;
    const isLoadingGetAllSlots = this.isLoadingGetAllSlots(this.props.loadingState);

    return (
      <section id="schedulingContainer" className={s.component}>
        {authenticatedPreferences &&
          <SchedulingRules
            data={authenticatedPreferences.appointmentSchedulingInstructions}
          />
        }

        {
          this.renderStrategizedFilters()
        }

        {isLoadingGetAllSlots ? (
          <div className={s.filtersContainer}>
            <p className={s.message}>
              Loading available appointments...
            </p>
            <LoadingState context="scheduling" />
          </div>
        ) : (
          <div>
            {this.state.appointmentSlots &&
            <div>
              <SchedulingCalendar
                handleTimeSlotSelection={this.handleTimeSlotSelection}
                dates={this.state.appointmentSlots}
              />
              <SchedulingNotes
                handleNotes={this.handleNotes}
              />
            </div>
            }
          </div>
        )}

        {this.state.getAllSlotsError &&
          <p className={cx(s.message, s.alert)}>
            {this.state.getAllSlotsError}
          </p>
        }

        <AppointmentRequest
          authenticatedPreferences={authenticatedPreferences}
          closeConfirmDialog={this.props.closeConfirmDialog}
          closeAlertDialog={this.props.closeAlertDialog}
          openConfirmDialog={this.props.openConfirmDialog}
          openSuccessDialog={this.props.openSuccessDialog}
          getUpcomingAppointments={this.props.getUpcomingAppointments}
          handleGetFilterLabels={this.handleGetFilterLabels}
          handleRequestAppointment={this.props.requestAppointment}
          hideFullPageLoadingState={this.props.hideFullPageLoadingState}
          resetSchedulingUi={this.resetSchedulingUi}
          request={this.state.appointmentRequest}
          showFullPageLoadingState={this.props.showFullPageLoadingState}
        />
      </section>
    );
  }
}

// This component needs to get and render it's own objects here rather than passed in from LandingPage
const mapStateToProps = state => ({
  appointments: state.appointments,
  loadingState: state.loadingState,
});

const mapDispatchToProps = dispatch => ({
  getAllSlots: (data, successCb, errorCb) => dispatch(getAllSlots(data, successCb, errorCb)),
  getFilter: (data, successCb, errorCb) => dispatch(getFilter(data, successCb, errorCb)),
  getFilterLabels: successCb => dispatch(getFilterLabels(successCb)),
  getUpcomingAppointments: () => dispatch(getUpcomingAppointments()),
  requestAppointment: (data, successCb, errorCb) => dispatch(requestAppointment(data, successCb, errorCb)),
  getAppointmentTimeRange: (data, successCb, errorCb) => dispatch(getAppointmentTimeRange(data, successCb, errorCb)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SchedulingContainer);



// WEBPACK FOOTER //
// ./src/components/Pages/Appointments/Scheduling/SchedulingContainer.js